<script lang="ts">
  import { flip } from 'svelte/animate';
  import type { Cell } from '../../lib/labs';
  import { slowMs } from './lab-session.svelte';

  /**
   * キューの盤面（layout-and-navigation.md §5: 横、左が先頭 = 出口、右が末尾 = 入口）。
   * 通った人は灰色で残し、head は配列の下に矢印で描く（labs.md ラボ 2）。
   */
  interface Props {
    items: readonly Cell[];
    head: number;
    /** 詰める方式（通った人は配列から消える）。 */
    shift?: boolean;
    /** 詰める方式で、これまでに通った人数（箱の識別に使う）。 */
    offset?: number;
    name?: string;
    /** 末尾が今並んだ。 */
    entered?: boolean;
    /** まだ通っていない人を光らせる（count）。 */
    glowWaiting?: boolean;
    /** 動きを減らす設定のときに、変わった所へ添える文字。 */
    changeTag?: string | null;
    compact?: boolean;
  }

  let {
    items,
    head,
    shift = false,
    offset = 0,
    name = 'queue',
    entered = false,
    glowWaiting = false,
    changeTag = null,
    compact = false,
  }: Props = $props();

  const boxes = $derived(items.map((v, i) => ({ v, i, key: offset + i })));
  const duration = $derived(slowMs());
</script>

<div class="queue-area" class:compact data-testid="queue-board">
  <div class="ends" aria-hidden="true"><span>← 出口（先頭）</span><span>入口（末尾） ←</span></div>
  <ol class="queue-row" aria-label="{name} の中身（左が先頭）" style="--head: {head}">
    {#if items.length === 0}
      <li class="empty-note">空です</li>
    {/if}
    {#each boxes as b (b.key)}
      {@const passed = b.i < head}
      {@const front = b.i === head}
      <li
        class="box"
        class:passed
        class:front
        class:enter={entered && b.i === items.length - 1}
        class:glow={glowWaiting && !passed}
        aria-label="{b.i + 1} 番目、値 {b.v}{passed ? '、通過済み' : ''}{front ? '、次に通る' : ''}"
        animate:flip={{ duration }}
      >
        <span class="val">{b.v}</span>
        <span class="idx" aria-hidden="true">[{b.i}]</span>
        {#if front && changeTag}<span class="change-tag">{changeTag}</span>{/if}
      </li>
    {/each}
  </ol>
  {#if !shift}
    <div class="head-row" aria-hidden="true">
      <span class="head-arrow" style="transform: translateX(calc({head} * (var(--box-w) + 6px)))"
        >↑ head</span
      >
    </div>
    <p class="visually-hidden">head = {head}</p>
  {/if}
</div>

<style>
  .queue-area {
    --box-w: 3rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--space-2);
    min-height: 15rem;
    padding: var(--space-4);
    background: var(--canvas);
    border-radius: var(--radius-md);
    overflow-x: auto;
  }
  .queue-area.compact {
    --box-w: 2.4rem;
    min-height: 8rem;
    padding: var(--space-3);
  }
  .ends {
    display: flex;
    justify-content: space-between;
    font-size: var(--fs-xs);
    color: var(--text-soft);
  }
  .queue-row {
    display: flex;
    gap: 6px;
    margin: 0;
    padding: 0 0 var(--space-3);
    list-style: none;
    min-height: calc(var(--box-w) + var(--space-3));
  }
  .box {
    flex: none;
    position: relative;
    display: grid;
    place-items: center;
    width: var(--box-w);
    height: var(--box-w);
    border-radius: var(--radius-md);
    background: var(--surface);
    border: 2px solid var(--border-strong);
    font-family: var(--font-mono);
    font-size: var(--fs-lg);
    font-weight: var(--fw-bold);
    font-variant-numeric: tabular-nums;
    transition:
      opacity var(--dur-slow),
      background var(--dur-fast);
  }
  .compact .box {
    font-size: var(--fs-md);
  }
  .box.passed {
    opacity: 0.45;
    border-style: dashed;
    color: var(--text-soft);
  }
  .box.front {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .box.glow {
    background: var(--code-line-hl);
  }
  .box.enter {
    animation: slide-in var(--dur-slow) var(--ease-out);
  }
  @keyframes slide-in {
    from {
      transform: translateX(2rem);
      opacity: 0;
    }
    to {
      transform: none;
      opacity: 1;
    }
  }
  :global(html[data-motion='reduce']) .box.enter {
    animation: none;
  }
  .idx {
    position: absolute;
    bottom: calc(-1 * var(--space-4));
    font-size: var(--fs-xs);
    color: var(--text-soft);
    font-family: var(--font-sans);
    font-weight: var(--fw-regular);
  }
  .head-row {
    height: 1.5rem;
    position: relative;
  }
  .head-arrow {
    display: inline-block;
    width: var(--box-w);
    text-align: center;
    font-size: var(--fs-xs);
    font-weight: var(--fw-bold);
    color: var(--accent);
    white-space: nowrap;
    transition: transform var(--dur-slow) var(--ease-out);
  }
  :global(html[data-motion='reduce']) .head-arrow {
    transition: none;
  }
  .change-tag {
    position: absolute;
    top: -0.8rem;
    padding: 0 var(--space-1);
    border-radius: var(--radius-sm);
    background: var(--accent);
    color: var(--text-on-accent);
    font-family: var(--font-sans);
    font-size: var(--fs-xs);
    font-weight: var(--fw-bold);
    white-space: nowrap;
  }
  .empty-note {
    color: var(--text-soft);
    font-size: var(--fs-sm);
    align-self: center;
  }
</style>
